/**
 * Midday Reset - Quick Return to Balance
 * 60-90 second guided reset with breath and anchor.
 */

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart, Sparkles } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MiddayResetPage = () => {
  const navigate = useNavigate();
  const { journeyId } = useParams();
  const [phase, setPhase] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [journey, setJourney] = useState(null);
  
  const getToken = () => localStorage.getItem("reclaim_token");

  // Reset steps
  const steps = [
    { duration: 5, text: "Pause...", instruction: "Stop what you're doing" },
    { duration: 8, text: "Place your hand on your heart...", instruction: "Feel its warmth" },
    { duration: 10, text: "Breathe in deeply...", instruction: "Fill your lungs" },
    { duration: 10, text: "Exhale slowly...", instruction: "Release tension" },
    { duration: 8, text: "I am returning to balance...", instruction: "Repeat silently" },
    { duration: 8, text: "One more breath...", instruction: "Deep and slow" },
    { duration: 6, text: "You are here...", instruction: "Present" },
    { duration: 5, text: "You are balanced...", instruction: "Centered" }
  ];

  useEffect(() => {
    loadJourney();
  }, [journeyId]);

  useEffect(() => {
    if (!isActive) return;

    const totalSteps = steps.length;
    if (phase >= totalSteps) {
      // Complete
      handleComplete();
      return;
    }

    const currentStep = steps[phase];
    const timer = setTimeout(() => {
      setPhase(prev => prev + 1);
    }, currentStep.duration * 1000);

    return () => clearTimeout(timer);
  }, [phase, isActive]);

  const loadJourney = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/balance/journey/${journeyId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setJourney(response.data);
    } catch (err) {
      console.error("Error loading journey:", err);
    }
  };

  const handleComplete = async () => {
    try {
      await axios.post(
        `${API_URL}/api/balance/journey/${journeyId}/session`,
        { day_number: journey?.progress?.current_day || 1, session_type: "midday_reset" },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
    } catch (err) {
      console.error("Error logging midday reset:", err);
    }
  };

  const gradient = journey?.gradient || ["#7C3AED", "#EC4899"];
  const currentStep = steps[phase];

  // Start screen
  if (!isActive) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
        {/* Close button */}
        <div className="p-6 flex justify-end">
          <button
            onClick={() => navigate(`/balance/${journeyId}`)}
            className="p-2 rounded-full bg-white/5 text-white/40 hover:text-white/80"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring" }}
            className="w-24 h-24 rounded-full mb-8 flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${gradient[0]}30, ${gradient[1]}30)`,
              border: `2px solid ${gradient[0]}50`
            }}
          >
            <Sparkles size={40} style={{ color: gradient[0] }} />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-2xl font-light text-white mb-3"
          >
            Return to Balance
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/50 mb-2"
          >
            A 60-second reset
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-white/30 text-sm mb-12 max-w-xs"
          >
            Whenever you feel pulled away from center, use this to return
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsActive(true)}
            className="px-10 py-4 rounded-full text-white font-medium"
            style={{
              background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`
            }}
          >
            Begin Reset
          </motion.button>
        </div>
      </div>
    );
  }

  // Complete screen
  if (phase >= steps.length) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center px-8 text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring" }}
          className="w-20 h-20 rounded-full mb-8 flex items-center justify-center bg-emerald-500/20"
        >
          <Heart size={36} className="text-emerald-400" />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-2xl font-light text-white mb-3"
        >
          You are balanced
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-white/50 mb-12"
        >
          Return whenever you need
        </motion.p>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          onClick={() => navigate(`/balance/${journeyId}`)}
          className="px-8 py-3 rounded-full bg-white/10 text-white/80 hover:bg-white/20 transition-colors"
        >
          Continue Journey
        </motion.button>
      </div>
    );
  }

  // Active reset
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center px-8 text-center relative overflow-hidden">
      {/* Ambient glow */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            `radial-gradient(circle at 50% 50%, ${gradient[0]}10, transparent 60%)`,
            `radial-gradient(circle at 50% 50%, ${gradient[1]}15, transparent 60%)`,
            `radial-gradient(circle at 50% 50%, ${gradient[0]}10, transparent 60%)`
          ]
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />

      {/* Breathing circle */}
      <motion.div
        animate={{
          scale: phase % 2 === 0 ? [1, 1.2, 1] : [1.2, 1, 1.2],
          opacity: [0.3, 0.6, 0.3]
        }}
        transition={{ duration: currentStep?.duration || 5, ease: "easeInOut" }}
        className="absolute w-64 h-64 rounded-full"
        style={{
          background: `radial-gradient(circle, ${gradient[0]}20, transparent 70%)`
        }}
      />

      {/* Main text */}
      <AnimatePresence mode="wait">
        <motion.div
          key={phase}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <p className="text-white text-2xl font-light mb-4">
            {currentStep?.text}
          </p>
          <p className="text-white/40 text-sm">
            {currentStep?.instruction}
          </p>
        </motion.div>
      </AnimatePresence>

      {/* Progress dots */}
      <div className="absolute bottom-12 flex gap-2">
        {steps.map((_, idx) => (
          <div
            key={idx}
            className={`w-2 h-2 rounded-full transition-colors ${
              idx <= phase ? "bg-white/60" : "bg-white/10"
            }`}
          />
        ))}
      </div>

      {/* Skip button */}
      <button
        onClick={() => {
          handleComplete();
          navigate(`/balance/${journeyId}`);
        }}
        className="absolute top-6 right-6 text-white/30 text-sm hover:text-white/60"
      >
        Skip
      </button>
    </div>
  );
};

export default MiddayResetPage;
